/**
 * # Heading
 *
 * A component for rendering headings with customizable styles and options.
 *
 * ## Props
 *
 * - `as` (optional): The HTML element type to be used for the heading. Defaults to `'div'`.
 * - `className` (optional): Additional CSS class name(s) for the heading.
 * - `decorative` (optional): Specifies whether the heading is decorative or not.
 * - `size` (optional): The size of the heading. Can be one of `'xl'`, `'l'`, `'m'`, `'s'`, `'xs'`, or `'xxs'`. Defaults to `'m'`.
 * - `uppercase` (optional): Specifies whether the heading should be displayed in uppercase.
 * - `withMargin` (optional): Specifies whether the heading should have margin.
 *
 * ## Usage
 *
 * ```tsx
 * import React from 'react';
 * import Heading from './Heading';
 *
 * const MyComponent = () => {
 *   return (
 *     <div>
 *       <Heading size="xl" uppercase>
 *         Hello, World!
 *       </Heading>
 *     </div>
 *   );
 * };
 *
 * export default MyComponent;
 * ```
 */

import React from 'react';
import classnames from 'classnames';
import './heading.scss';

export interface Props extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: React.ElementType;
  children: React.ReactNode;
  className?: string;
  decorative?: boolean;
  size?: 'xl' | 'l' | 'm' | 's' | 'xs' | 'xxs';
  uppercase?: boolean;
  withMargin?: boolean;
}

export const Heading = ({
  as = 'div',
  className,
  decorative,
  size = 'm',
  uppercase,
  withMargin,
  ...rest
}: Props) => {
  const WrapperElement = as;
  return (
    <WrapperElement
      className={classnames(className, 'a-heading', `a-heading--${size}`, {
        'a-heading--with-margin': withMargin,
        'a-heading--decorative': decorative,
        [`a-heading--decorative-${size}`]: decorative && size,
        'a-heading--uppercase': uppercase
      })}
      {...rest}
    />
  );
};

export default Heading;
